import React, { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, CalculatorOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

const money = (v: unknown) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EosbFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions({ employment_status: null });
  const [preview, setPreview] = useState<any>(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const requestException = Form.useWatch('request_gratuity_exception', form);
  const basicOverride = Form.useWatch('basic_salary', form);

  const calculate = async () => {
    try {
      const v = await form.validateFields(['employee_id', 'exit_type', 'last_working_date']);
      setCalcLoading(true);
      const unpaid = form.getFieldValue('unpaid_leave_days');
      const result = await hrmsService.calculateEosb({
        employee_id: v.employee_id,
        exit_type: v.exit_type,
        last_working_date: v.last_working_date.format('YYYY-MM-DD'),
        basic_salary: form.getFieldValue('basic_salary'),
        basic_salary_override_reason: form.getFieldValue('basic_salary_override_reason'),
        basic_salary_override_effective_date: form
          .getFieldValue('basic_salary_override_effective_date')
          ?.format?.('YYYY-MM-DD'),
        unpaid_leave_days: unpaid,
        unpaid_leave_correction_reason: form.getFieldValue('unpaid_leave_correction_reason'),
        auto_unpaid_leave: unpaid == null,
        leave_encashment: form.getFieldValue('leave_encashment'),
        other_dues: form.getFieldValue('other_dues'),
        recoveries: form.getFieldValue('recoveries'),
        request_gratuity_exception: form.getFieldValue('request_gratuity_exception'),
        gratuity_exception_justification: form.getFieldValue('gratuity_exception_justification'),
      });
      setPreview(result);
      if (result?.gratuity_amount != null) form.setFieldValue('gratuity_amount', result.gratuity_amount);
      if (result?.recoveries != null && form.getFieldValue('recoveries') == null) {
        form.setFieldValue('recoveries', result.recoveries);
      }
      if (result?.unpaid_leave?.suggested_days != null && unpaid == null) {
        form.setFieldValue('unpaid_leave_days', result.unpaid_leave.suggested_days);
      }
      message.success('Preview calculated');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Calculate failed');
    } finally {
      setCalcLoading(false);
    }
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const created = await hrmsService.createEosbSettlement({
        employee_id: v.employee_id,
        exit_type: v.exit_type,
        last_working_date: v.last_working_date.format('YYYY-MM-DD'),
        basic_salary: v.basic_salary,
        basic_salary_override_reason: v.basic_salary_override_reason,
        basic_salary_override_effective_date: v.basic_salary_override_effective_date?.format?.(
          'YYYY-MM-DD'
        ),
        unpaid_leave_days: v.unpaid_leave_days,
        unpaid_leave_correction_reason: v.unpaid_leave_correction_reason,
        leave_encashment: v.leave_encashment,
        other_dues: v.other_dues,
        recoveries: v.recoveries,
        notes: v.notes,
        request_gratuity_exception: Boolean(v.request_gratuity_exception),
        gratuity_exception_justification: v.gratuity_exception_justification,
      });
      message.success('Settlement created as Draft — submit for approval before financial consequence');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.eosbSettlements() });
      navigate(`/hr/eosb/${created?.id || ''}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Create failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="New EOSB settlement"
          subtitle="Calculate preview, then create Draft settlement (Approval Runtime required for financial consequence)"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/eosb')}>
                Cancel
              </Button>
              <Button icon={<CalculatorOutlined />} loading={calcLoading} onClick={calculate}>
                Calculate preview
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Create settlement
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical" initialValues={{ exit_type: 'Resignation' }}>
        <Card title="Exit details" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
              <Text type="secondary">
                Tip: open{' '}
                <a
                  onClick={() => {
                    const id = form.getFieldValue('employee_id');
                    if (id) navigate(`/hr/eosb/profile/${id}`);
                  }}
                >
                  employee EOSB profile
                </a>{' '}
                for full ledger.
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="exit_type" label="Exit type" rules={[{ required: true }]}>
                <Select
                  options={[
                    'Resignation',
                    'Termination',
                    'EndOfContract',
                    'Retirement',
                    'Death',
                    'Transfer',
                  ].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="last_working_date" label="Last working date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Salary basis (settlement override — does not change Employee Master)" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="basic_salary" label="Basic salary override">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="basic_salary_override_reason"
                label="Override reason"
                rules={basicOverride != null ? [{ required: true, message: 'Required for override' }] : []}
              >
                <Input placeholder="Why last basic differs from Employee Master" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="basic_salary_override_effective_date"
                label="Override effective date"
                rules={basicOverride != null ? [{ required: true, message: 'Required for override' }] : []}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary">
            Material overrides require settlement approval. Previously approved accounting valuations are not
            changed.
          </Text>
        </Card>

        <Card title="Excluded / unpaid service" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="unpaid_leave_days"
                label="Excluded service days"
                extra="Leave blank to use system suggestion (approved unpaid applications preferred)"
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="unpaid_leave_correction_reason"
                label="Correction reason"
                extra="Required when applied days differ from suggestion"
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          {preview?.unpaid_leave && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message={`Suggested ${preview.unpaid_leave.suggested_days} day(s) from ${preview.unpaid_leave.preferred_source}`}
              description={preview.unpaid_leave.overlap_note}
            />
          )}
        </Card>

        <Card title="Request Gratuity Exception" style={{ marginBottom: 16 }}>
          <Form.Item name="request_gratuity_exception" valuePropName="checked">
            <Checkbox>
              Request Gratuity Exception (alternative scheme / pension flag — does not bypass statutory
              min-service rules)
            </Checkbox>
          </Form.Item>
          {requestException && (
            <Form.Item
              name="gratuity_exception_justification"
              label="Business and legal justification"
              rules={[{ required: true, min: 20, message: 'Min 20 characters' }]}
            >
              <Input.TextArea
                rows={3}
                placeholder="Confirm no double entitlement under traditional gratuity and alternative savings/pension. Escalate unsupported legal scenarios."
              />
            </Form.Item>
          )}
        </Card>

        {preview?.warnings?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Governance warnings"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {preview.warnings.map((w: string) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            }
          />
        )}

        {preview && (
          <Card title="Calculation preview" style={{ marginBottom: 16 }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Text type="secondary">Gratuity</Text>
                <div>
                  <Text strong>{money(preview.gratuity_amount)}</Text>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">Qualifying years</Text>
                <div>
                  <Text strong>{Number(preview.service_years || 0).toFixed(4)}</Text>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">Salary basis</Text>
                <div>
                  <Text strong>
                    {preview.basic_salary_override
                      ? `Override ${money(preview.basic_salary)} (Master ${money(
                          preview.basic_salary_employee_record
                        )})`
                      : money(preview.basic_salary)}
                  </Text>
                </div>
              </Col>
            </Row>
            {preview.valuation_disclaimer && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={preview.valuation_disclaimer}
              />
            )}
            <Table
              size="small"
              pagination={false}
              rowKey={(r: any) => String(r.step)}
              dataSource={preview.calculation_steps || []}
              columns={[
                { title: '#', dataIndex: 'step', width: 50 },
                { title: 'Step', dataIndex: 'title', width: 180 },
                { title: 'Detail', dataIndex: 'detail' },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  width: 110,
                  render: (v) => (v != null ? money(v) : '—'),
                },
              ]}
            />
          </Card>
        )}

        <Card title="Settlement amounts">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="gratuity_amount" label="Gratuity (from formula — informational)">
                <InputNumber style={{ width: '100%' }} min={0} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="leave_encashment" label="Leave encashment">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="other_dues" label="Other dues">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="recoveries" label="Recoveries">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary">
            Gratuity is recalculated on create. GL handoff utilizes recognized EOSB liability against gratuity
            only — leave encashment uses leave/payroll services.
          </Text>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default EosbFormPage;
